import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { usePortalSafety } from '@/hooks/use-projects';
import { apiClient } from '@/lib/api-client';
import type { PortalSafetyDocument } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

const statusColors: Record<string, string> = {
  approved: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
};

const PORTAL_API = '/api/v1/projects/portal';
const PORTAL_API_BASE_URL = apiClient.defaults.baseURL ?? '';

function SafetyDocTable({
  title,
  icon,
  documents,
  pdfPathSegment,
  projectId,
}: {
  title: string;
  icon: React.ReactNode;
  documents: PortalSafetyDocument[];
  pdfPathSegment: string;
  projectId: string;
}) {
  if (documents.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">PDF</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell className="font-medium">{doc.title}</TableCell>
                <TableCell className="text-gray-600">
                  {formatDateOnly(doc.date)}
                </TableCell>
                <TableCell className="text-gray-600">{doc.location}</TableCell>
                <TableCell>
                  <Badge className={statusColors[doc.status] ?? 'bg-gray-100 text-gray-800'}>
                    {doc.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={`${PORTAL_API_BASE_URL}${PORTAL_API}/projects/${projectId}/safety/${pdfPathSegment}/${doc.id}/pdf/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </a>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function PortalProjectSafetyPage() {
  const { token, projectId } = useParams<{ token: string; projectId: string }>();
  const { data, isLoading } = usePortalSafety(token ?? '', Number(projectId) || 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Not Found</h1>
          <p className="text-gray-600">Unable to load safety documents for this project.</p>
        </div>
      </div>
    );
  }

  const totalDocs =
    data.jsas.length + data.safe_work_plans.length + data.rescue_plans.length;

  return (
    <>
      <nav className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/portal/${token}`}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm text-gray-500">Safety Documents</p>
                  <p className="text-2xl font-bold">{totalDocs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-500">Project</p>
                  <p className="text-lg font-semibold text-gray-900">{data.project_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {totalDocs === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No approved safety documents available for this project yet.
            </CardContent>
          </Card>
        )}

        <SafetyDocTable
          title="Job Safety Analyses (JSA)"
          icon={<ShieldCheck className="h-5 w-5 text-green-600" />}
          documents={data.jsas}
          pdfPathSegment="jsa"
          projectId={projectId ?? ''}
        />

        <SafetyDocTable
          title="Safe Work Plans"
          icon={<FileText className="h-5 w-5 text-blue-600" />}
          documents={data.safe_work_plans}
          pdfPathSegment="swp"
          projectId={projectId ?? ''}
        />

        <SafetyDocTable
          title="Rescue Plans"
          icon={<FileText className="h-5 w-5 text-orange-600" />}
          documents={data.rescue_plans}
          pdfPathSegment="rescue-plan"
          projectId={projectId ?? ''}
        />
      </main>
    </>
  );
}
